import {
  type AreaData,
  type AreaRunHistoryEntry,
  type AreaScheduleEntry,
  type AreaStateName,
  isUnassignedUserId,
  type UserId,
} from '../model'
import { getCreatedAt } from './audit'
import { isDefined, isEmpty, isNotDefined, isNotEmpty } from './isEmpty'

export function hasUnassignedOwner(userIds: UserId[] | undefined): boolean {
  return isNotEmpty(userIds) && userIds.some(isUnassignedUserId)
}

// The "no owner" value only describes accounts today. Once an area assigns them they stop matching it.
export function withoutUnassignedOwner(userIds: UserId[] | undefined): UserId[] | undefined {
  const remaining = userIds?.filter((userId) => !isUnassignedUserId(userId))
  return isEmpty(remaining) ? undefined : remaining
}

export function isAreaAssigned(area: Pick<AreaData, 'userIds' | 'teamId'>): boolean {
  if (area.teamId) return true
  return isNotEmpty(area.userIds)
}

export function getAreaStateName(area: AreaData): AreaStateName {
  const isAccountAssignment = isDefined(area.assignmentConfig)
  if (!isAccountAssignment) return hasAreaAssignmentTarget(area) ? 'assigned' : 'draft'

  const hasTarget = hasAreaAssignmentTarget(area)
  const scheduledDate = getNextFutureAreaAssign(area)
  const expiryDate = getNextFutureAreaUnassign(area)
  const isCurrentAssigned = getIsAreaCurrentlyAssigned(area)

  if (!hasTarget) return 'draft'
  if (isDefined(scheduledDate) && !isCurrentAssigned) return 'scheduled'
  if (isCurrentAssigned && isDefined(expiryDate)) return 'expiring'
  if (isCurrentAssigned) return 'assigned'
  return 'unassigned'
}

export function hasAreaAssignmentTarget(area: Pick<AreaData, 'teamId' | 'userIds'>): boolean {
  return isDefined(area.teamId) || isNotEmpty(area.userIds)
}

export function getIsAreaCurrentlyAssigned(area: AreaData): boolean {
  if (isNotDefined(area.assignmentConfig)) return hasAreaAssignmentTarget(area)
  const lastRun = getLastAreaRun(area)
  return isDefined(lastRun) && lastRun.type !== 'unassign'
}

export function getLastAreaRun(area: AreaData): AreaRunHistoryEntry | undefined {
  if (isEmpty(area.runHistory)) return
  return [...area.runHistory].sort((a, b) => b.timestamp - a.timestamp).at(0)
}

export function getAreaLastAssignmentAt(area: AreaData): number | undefined {
  if (isNotDefined(area.assignmentConfig)) return getCreatedAt(area)
  const lastRun = getLastAreaRun(area)
  if (isNotDefined(lastRun) || lastRun.type === 'unassign') return
  return lastRun.timestamp
}

export function getAreaExpiresAt(area: AreaData): number | undefined {
  return area.schedule?.find((s): s is Extract<AreaScheduleEntry, { type: 'unassign' }> => s.type === 'unassign')?.at
}

export function getNextFutureAreaAssign(area: AreaData): number | undefined {
  const now = Date.now()
  return area.schedule?.filter((s) => (s.type === 'assign' || s.type === 'reassign') && s.at > now).sort((a, b) => a.at - b.at)[0]?.at
}

export function getNextFutureAreaUnassign(area: AreaData): number | undefined {
  const now = Date.now()
  return area.schedule?.filter((s) => s.type === 'unassign' && s.at > now).sort((a, b) => a.at - b.at)[0]?.at
}
